import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AspectRatioOutlinedIcon from "@mui/icons-material/AspectRatioOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import Switch from "components/Switch";
import Button from "components/Button";
import COLORS from "shared/constants/colors";
import { showLoader, hideLoader } from "actions/loader";
import { addCart } from "actions/dtfCart";
import { POPULAR_SIZES } from "pages/dtf-transfers/by-size/data";
import {
  getTotalPrice,
  getBaseUnitPrice,
  getCuttingUnitPrice,
  getArtworkCheckFee,
  clampDimension,
  getAspectLinkedDimensions,
  applyCloudinaryBackgroundRemoval,
  preloadCloudinaryImage,
  buildCartItem,
  canAddToCart,
} from "pages/dtf-transfers/by-size/helpers";
import {
  StepCard,
  StepTitle,
  CustomizeLayout,
  AiToolsPanel,
  AiToolsTitle,
  AiToolRow,
  PreviewPanel,
  PreviewToolbar,
  PreviewCanvas,
  PreviewImage,
  PreviewCaption,
  SizeTabs,
  SizeTab,
  SizeHelpText,
  SizeFieldsRow,
  SizeField,
  SizeFieldLabel,
  SizeInput,
  PriceValue,
  AddSizeLink,
  CartActions,
  CartSuccessMessage,
} from "styles/pages/dtf-transfers/by-size";

const UploadedArtworkEditor = ({ artwork, onChange, onRemove }) => {
  const dispatch = useDispatch();
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const isCartLoading = useSelector((state) => state?.dtfCart?.loading);
  const [cartMessage, setCartMessage] = useState("");
  const [cartError, setCartError] = useState("");
  const unitPrice = getBaseUnitPrice(artwork.width, artwork.height);
  const cuttingPrice = getCuttingUnitPrice(artwork.cutSeparately);
  const artworkCheckPrice = getArtworkCheckFee(artwork.artworkCheck);
  const totalPrice = getTotalPrice(
    artwork.width,
    artwork.height,
    artwork.quantity,
    artwork.cutSeparately,
    artwork.artworkCheck,
  );

  const updateArtwork = (updates) => {
    onChange({ ...artwork, ...updates });
  };

  const handleDimensionChange = (field, value) => {
    if (!artwork.keepAspectRatio) {
      updateArtwork({ [field]: clampDimension(value) });
      return;
    }

    const aspectRatio =
      Number(artwork.height) > 0
        ? Number(artwork.width) / Number(artwork.height)
        : 1;

    updateArtwork(getAspectLinkedDimensions(field, value, aspectRatio));
  };

  const handlePopularSizeChange = (event) => {
    const selected = POPULAR_SIZES.find(
      (size) => size.label === event.target.value,
    );

    if (!selected) {
      updateArtwork({ popularSize: "", sizeTab: "popular" });
      return;
    }

    updateArtwork({
      popularSize: selected.label,
      width: selected.width,
      height: selected.height,
      sizeTab: "popular",
    });
  };

  const handleRemoveBackgroundChange = async (event) => {
    const checked = event.target.checked;
    const originalUrl =
      artwork.originalSecureUrl || artwork.secureUrl || artwork.previewUrl;

    if (!originalUrl) return;

    if (!checked) {
      updateArtwork({
        removeBackground: false,
        previewUrl: originalUrl,
        secureUrl: originalUrl,
      });
      return;
    }

    dispatch(showLoader());

    try {
      const backgroundRemovedUrl =
        applyCloudinaryBackgroundRemoval(originalUrl);
      const loadedUrl = await preloadCloudinaryImage(backgroundRemovedUrl);

      updateArtwork({
        removeBackground: true,
        previewUrl: loadedUrl,
        secureUrl: loadedUrl,
        originalSecureUrl: originalUrl,
      });
    } catch (error) {
      console.error(error);
      updateArtwork({ removeBackground: false });
    } finally {
      dispatch(hideLoader());
    }
  };

  const isAddToCartDisabled =
    !canAddToCart(artwork) || !emailid || isCartLoading;

  const handleAddToCart = async () => {
    if (isAddToCartDisabled) return;

    setCartError("");
    setCartMessage("");

    dispatch(showLoader());

    try {
      const payload = buildCartItem(artwork);
      const result = await dispatch(addCart(emailid, token, payload));

      if (result?.error) {
        setCartError(result.message || "Failed to add item to cart.");
        return;
      }

      setCartMessage("Added to cart successfully.");
      window.setTimeout(() => setCartMessage(""), 3000);
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <>
      <StepCard>
        <StepTitle>
          <span>Step 1:</span> Customize Your Image
        </StepTitle>

        <CustomizeLayout>
          <AiToolsPanel>
            <AiToolsTitle>Options</AiToolsTitle>

            <AiToolRow>
              <span>Keep Aspect Ratio</span>
              <Switch
                checked={Boolean(artwork.keepAspectRatio)}
                handleChange={(event) =>
                  updateArtwork({ keepAspectRatio: event.target.checked })
                }
              />
            </AiToolRow>

            <AiToolRow>
              <span>Remove Background</span>
              <Switch
                checked={artwork.removeBackground}
                disabled={!artwork.secureUrl && !artwork.originalSecureUrl}
                handleChange={handleRemoveBackgroundChange}
              />
            </AiToolRow>

            <AiToolRow>
              <span>Cut Separately ($0.50 ea)</span>
              <Switch
                checked={artwork.cutSeparately}
                handleChange={(event) =>
                  updateArtwork({ cutSeparately: event.target.checked })
                }
              />
            </AiToolRow>

            <AiToolRow>
              <span>Artwork Check ($10)</span>
              <Switch
                checked={artwork.artworkCheck}
                handleChange={(event) =>
                  updateArtwork({ artworkCheck: event.target.checked })
                }
              />
            </AiToolRow>

            {/* <AiToolRow>
              <span>Upscale</span>
              <Switch
                checked={artwork.upscale}
                handleChange={(event) =>
                  updateArtwork({ upscale: event.target.checked })
                }
              />
            </AiToolRow>

            <EditWithAiButton type="button">
              <AutoAwesomeIcon />
              Edit with AI
            </EditWithAiButton> */}
          </AiToolsPanel>

          <PreviewPanel>
            <PreviewToolbar>
              <button type="button" onClick={onRemove} aria-label="Remove file">
                <DeleteOutlineIcon />
              </button>
              <button type="button" aria-label="Zoom image">
                <ZoomInOutlinedIcon />
              </button>
            </PreviewToolbar>

            <PreviewCanvas>
              <PreviewImage
                src={artwork.previewUrl}
                alt={
                  artwork.fileName || artwork.file?.name || "Uploaded artwork"
                }
              />
            </PreviewCanvas>

            <PreviewCaption>
              Preview: The image will print as shown above.
            </PreviewCaption>
          </PreviewPanel>
        </CustomizeLayout>
      </StepCard>

      <StepCard>
        <StepTitle>
          <span>Step 2:</span> Choose your Transfer Sizes
        </StepTitle>

        <SizeTabs>
          <SizeTab
            type="button"
            $active={artwork.sizeTab === "custom"}
            onClick={() => updateArtwork({ sizeTab: "custom" })}
          >
            <AspectRatioOutlinedIcon />
            Custom Size
          </SizeTab>
          {/* <SizeTab
            type="button"
            $active={artwork.sizeTab === "popular"}
            onClick={() => updateArtwork({ sizeTab: "popular" })}
          >
            <StarOutlineOutlinedIcon />
            Popular Size
          </SizeTab> */}
        </SizeTabs>

        {artwork.sizeTab === "popular" ? (
          <SizeFieldsRow>
            <SizeField $wide>
              <SizeFieldLabel>Select a popular size</SizeFieldLabel>
              <select
                value={artwork.popularSize}
                onChange={handlePopularSizeChange}
              >
                <option value="">Select a popular size</option>
                {POPULAR_SIZES.map((size) => (
                  <option key={size.label} value={size.label}>
                    {size.label}
                  </option>
                ))}
              </select>
            </SizeField>
          </SizeFieldsRow>
        ) : null}

        <SizeFieldsRow>
          <SizeField>
            <SizeFieldLabel>Width (inches)</SizeFieldLabel>
            <SizeInput
              type="number"
              min="0.25"
              max="22"
              step="0.01"
              value={artwork.width}
              onChange={(event) =>
                handleDimensionChange("width", event.target.value)
              }
            />
          </SizeField>

          <SizeField>
            <SizeFieldLabel>Height (inches)</SizeFieldLabel>
            <SizeInput
              type="number"
              min="0.25"
              max="22"
              step="0.01"
              value={artwork.height}
              onChange={(event) =>
                handleDimensionChange("height", event.target.value)
              }
            />
          </SizeField>

          <SizeField>
            <SizeFieldLabel>Qty</SizeFieldLabel>
            <SizeInput
              type="number"
              min="1"
              step="1"
              value={artwork.quantity}
              onChange={(event) =>
                updateArtwork({
                  quantity: Math.max(1, Number(event.target.value) || 1),
                })
              }
            />
          </SizeField>

          <SizeField>
            <SizeFieldLabel>Price</SizeFieldLabel>
            <PriceValue>${unitPrice.toFixed(2)} ea</PriceValue>
          </SizeField>

          <SizeField>
            <SizeFieldLabel>Cutting Price</SizeFieldLabel>
            <PriceValue>${cuttingPrice.toFixed(2)} ea</PriceValue>
          </SizeField>

          <SizeField>
            <SizeFieldLabel>Artwork Check</SizeFieldLabel>
            <PriceValue>${artworkCheckPrice.toFixed(2)}</PriceValue>
          </SizeField>

          <SizeField>
            <SizeFieldLabel>Total</SizeFieldLabel>
            <PriceValue $bold>${totalPrice.toFixed(2)}</PriceValue>
          </SizeField>
        </SizeFieldsRow>

        {/* <AddSizeLink
          type="button"
          onClick={() =>
            updateArtwork({
              extraSizes: [
                ...artwork.extraSizes,
                {
                  id: Date.now(),
                  width: artwork.width,
                  height: artwork.height,
                  quantity: 1,
                },
              ],
            })
          }
        >
          + Add another size of this design
        </AddSizeLink> */}

        <CartActions>
          <Button
            title="Add to Cart"
            disabled={isAddToCartDisabled}
            startIcon={<ShoppingCartOutlinedIcon />}
            onClick={handleAddToCart}
            containerStyle={{
              backgroundColor: COLORS.BLUE,
              color: COLORS.WHITE,
              textTransform: "none",
              borderRadius: "6px",
              padding: "10px 24px",
              fontSize: "15px",
              opacity: isAddToCartDisabled ? 0.5 : 1,
            }}
          />
          {cartMessage ? (
            <CartSuccessMessage>{cartMessage}</CartSuccessMessage>
          ) : null}
          {cartError ? (
            <CartSuccessMessage style={{ color: "#d32f2f" }}>
              {cartError}
            </CartSuccessMessage>
          ) : null}
        </CartActions>
      </StepCard>
    </>
  );
};

export default UploadedArtworkEditor;
